import * as ReactDOM from 'react-dom';
import * as React from 'react';

import {forOwn, cloneDeep, isEqual, mergeWith} from 'lodash';

import {stateProxyHandler} from './stateProxyHandler';
import {useSyncExternalStoreWithSelector} from './useSyncExternalStoreWithSelector';

const {useDebugValue} = React;

type Listener = () => void;

type EqualityFn<T> = (a: T, b: T) => boolean;

const refEquality: EqualityFn<unknown> = (a, b) => a === b;

type MergeObject = object & {
  __updated?: boolean;
};

const removeAllProxyMarkers = (obj: MergeObject) => {
  delete obj?.__updated;
  forOwn(obj, (value, _key) => {
    if (typeof value === 'object' && value !== null) {
      removeAllProxyMarkers(value);
    }
  });
};

/*
  Object recursive merging of changes onto the source object

  Designed as a depth-first traversal of potentially nested objects
    with custom merging to retain Object.is equality for unchanged objects
*/
const mergeFn = (
  objValue: MergeObject,
  srcValue: MergeObject | null,
  _key: string,
  _obj: MergeObject,
  src: MergeObject,
): MergeObject | null => {
  if (
    typeof srcValue === 'object' &&
    srcValue != null &&
    srcValue.__updated !== true
  ) {
    // if objValue is undefined, we want to just return srcValue
    // because _.mergeWith function turns an array into a dictionary
    if (objValue == null) {
      return cloneDeep(srcValue);
    }

    // if replacing an array instead of values in the array
    // eg, state.myArr = [1, 2] instead of state.myArr[0] = 3
    // then the lodash merge applies changes to the original array
    // this also occurs for plain objects too
    // which doesn't replace the array and thus won't trigger useSelector listeners on the array
    let isReplacement = false;
    if (
      ((Array.isArray(objValue) && Array.isArray(srcValue)) ||
        (typeof objValue === 'object' &&
          !Array.isArray(objValue) &&
          typeof srcValue === 'object' &&
          !Array.isArray(srcValue))) &&
      !Object.is(objValue, srcValue) &&
      !isEqual(objValue, srcValue)
    ) {
      isReplacement = true;
    }

    // eslint-disable-next-line no-param-reassign
    srcValue = srcValue as MergeObject;

    /* We're eligible for an optimized merge, where we re-use objValue so that
    Object.is() equality returns true, preventing unnecessary re-renders. */
    const mergeRes = mergeWith(objValue, srcValue, mergeFn);

    // if a descendant was updated and bubbled up after merge
    //   so that ancestor listeners are triggered for changes
    if (srcValue && (srcValue.__updated || isReplacement)) {
      // remove the proxy marker to keep private
      delete objValue.__updated;
      delete srcValue.__updated;

      // pass the proxy upwards for DFS traversal
      if (Object.isExtensible(src)) {
        src.__updated = true;
      }

      // object cloning is to break Object.is() equality tests so updates trigger
      // each object is cloned and not just ancestor in case listeners are attached anywhere in tree
      if (isReplacement) {
        return cloneDeep(srcValue);
      }
      return cloneDeep(objValue);
    }
    return mergeRes;
  } else if (typeof srcValue === 'object' && srcValue != null) {
    /* We're not eligible for an optimized merge; either srcValue.__updated is set,
    or objValue and srcValue have different types. */

    // remove the proxy marker to keep private. this has to be recursive because
    // there might have been updates to both this object and some of its children
    removeAllProxyMarkers(srcValue);

    // pass the proxy upwards for DFS traversal
    if (Object.isExtensible(src)) {
      src.__updated = true;
    }

    return srcValue;
  }

  return srcValue;
};

/*
  Docs at go/billing-state-manager

  Primary entrypoint for creating an 'external' store
    where external store represents state outside of the react tree
    as opposed to context, useReducer, and useState

  This is a lightweight dependency-free solution relative to Redux
    and intended for use cases where state is widely shared
    yet prop-drilling is cumbersome
    and context contains significant performance penalties on oft-changing state
    or at least stringent memoization efforts
*/
export function createStore<StoreState>(initialState: StoreState) {
  const listeners = new Set<Listener>();

  const initialStateSnapshot = cloneDeep(initialState);
  let currentState = cloneDeep(initialState);
  const store = {
    /** Replace the entire state for the store. Typically you want to use setValue instead to efficiently trigger listeners. This type must conform to the typescript type of the initial state. */
    set(newState: StoreState) {
      currentState = newState;
      // this is 'unstable' because its an implementation detail and will eventually not be needed
      //   react core recommends using it so it won't just 'break' in a minor patch
      //    https://github.com/facebook/react/issues/16387#issuecomment-521623662
      //    https://github.com/facebook/react/issues/16377
      //   react uses it internally, see:
      //     https://stackoverflow.com/questions/48563650/does-react-keep-the-order-for-state-updates/48610973#48610973
      ReactDOM.unstable_batchedUpdates(() => {
        listeners.forEach((listener) => listener());
      });
    },
    /** Update state with setValue, which provides efficient change detection through immutable data structures like Immer. */
    setValue(
      fn: (state: StoreState) => void,
      callback?: (state: StoreState) => void,
    ) {
      const currentStateClone = cloneDeep(currentState);
      const stateProxy = new Proxy(
        currentStateClone as object,
        stateProxyHandler,
      ) as StoreState;
      stateProxyHandler.setValue = false;

      // run the client handler
      fn(stateProxy);

      // quick no-op if no changes
      if (stateProxyHandler.setValue) {
        mergeWith(currentState, currentStateClone, mergeFn);
        store.set({...currentState});
      }

      if (callback) {
        callback(currentState);
      }
    },
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /** returns the full store object state. Typically you want useSelector instead to listen to changes to state as a hook.  This is useful for single snapshot queries. */
    getState(): StoreState {
      return currentState;
    },
    getSubscriberCount() {
      return listeners.size;
    },
    /** Resets the store to the initial state. */
    useSelector<Selected>(
      selector: (state: StoreState) => Selected,
      equalityFn: EqualityFn<Selected> = refEquality,
    ): Selected {
      if (process.env.NODE_ENV !== 'production') {
        if (!selector) {
          throw new Error(`You must pass a selector to useSelector`);
        }
        if (typeof selector !== 'function') {
          throw new Error(
            `You must pass a function as a selector to useSelector`,
          );
        }
        if (typeof equalityFn !== 'function') {
          throw new Error(
            `You must pass a function as an equality function to useSelector`,
          );
        }
      }

      const selectedState = useSyncExternalStoreWithSelector(
        store.subscribe,
        store.getState,
        null, // server state for future SSR
        selector,
        equalityFn,
      );

      useDebugValue(selectedState);

      return selectedState;
    },
    /** Resets the store to the initial state. */
    reset: () => {
      // reclone so future updates do not reference
      store.set(cloneDeep(initialStateSnapshot));
    },
  };

  return store;
}
